import React, { useState, useEffect, useRef, useMemo } from "react";
import { toast } from "react-toastify";
import { httpService } from "../../services/httpService";
import LoaderSpiner from "../../components/LoaderSpiner/LoaderSpiner";
import { FaIndianRupeeSign } from "react-icons/fa6";
import { FaSearch } from "react-icons/fa";
import {
  XMarkIcon,
  PlusIcon,
  MinusIcon,
  TrashIcon,
  ChevronDownIcon,
  ChevronRightIcon,
} from "@heroicons/react/24/outline";
import InfoTooltip from "../../components/common/InfoTooltip";
import ProductDrawer from "../../components/inventory/ProductDrawer";

const makeRowId = (seed) =>
  `${seed}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

const extractId = (value) => {
  if (!value) return null;
  if (typeof value === "string") return value;
  if (typeof value === "object" && value.$oid) return value.$oid;
  if (typeof value === "object" && value._id) return extractId(value._id);
  if (typeof value === "object" && value.toString && value.toString() !== "[object Object]") {
    return value.toString();
  }
  if (typeof value === "object" && value.id) return extractId(value.id);
  return null;
};

// Helper to check if procedure has products
const hasProducts = (procedure) => {
  return procedure.products && procedure.products.length > 0;
};

// Simplified - only fixed price now
const getProcedurePrice = (procedure) => {
  return procedure.fixed_price || 0;
};

const TreatmentPlanDrawer = ({ isOpen, planId, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [proceduresList, setProceduresList] = useState([]);
  const [expandedProcedures, setExpandedProcedures] = useState({});
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    procedures: [],
  });

  // ── Searchable procedure selector ──────────────────────────────────────
  const [procedureSearchTerm, setProcedureSearchTerm] = useState("");
  const [procedureSearchResults, setProcedureSearchResults] = useState([]);
  const [isSearchingProcedures, setIsSearchingProcedures] = useState(false);
  const [showProcedureDropdown, setShowProcedureDropdown] = useState(false);
  const procedureInputRef = useRef(null);
  const procedureDropdownRef = useRef(null);
  const [showAddProductDrawer, setShowAddProductDrawer] = useState(false);
  // ── plan-level extra products ─────────────────────────────────────────
  const [planProducts, setPlanProducts] = useState([]);
  const [productSearchTerm, setProductSearchTerm] = useState("");
  const [productSearchResults, setProductSearchResults] = useState([]);
  const [isSearchingProducts, setIsSearchingProducts] = useState(false);
  const [showProductDropdown, setShowProductDropdown] = useState(false);
  const productInputRef = useRef(null);
  const productDropdownRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      fetchProcedures();
      if (planId) {
        fetchPlanDetails();
      } else {
        resetForm();
      }
    }
  }, [isOpen, planId]);

  // click outside to close dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        procedureDropdownRef.current &&
        !procedureDropdownRef.current.contains(event.target) &&
        procedureInputRef.current &&
        !procedureInputRef.current.contains(event.target)
      ) {
        setShowProcedureDropdown(false);
      }
      if (
        productDropdownRef.current &&
        !productDropdownRef.current.contains(event.target) &&
        productInputRef.current &&
        !productInputRef.current.contains(event.target)
      ) {
        setShowProductDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const resetForm = () => {
    setFormData({
      name: "",
      description: "",
      procedures: [],
    });
    setPlanProducts([]);
    setProductSearchTerm("");
    setProductSearchResults([]);
    setProcedureSearchTerm("");
    setProcedureSearchResults([]);
    setExpandedProcedures({});
  };

  const fetchProcedures = async () => {
    try {
      const response = await httpService.get(
        `/vendorvetclinicservice/get-service-procedures?status=active`
      );
      if (response.code === 200) {
        setProceduresList(response?.data || []);
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to load procedures");
    }
  };

  // ── Search procedures (client-side filtering) ─────────────────────────
  const searchProcedures = (searchTerm) => {
    const term = (searchTerm || "").trim().toLowerCase();

    const results =
      term.length === 0
        ? proceduresList
        : proceduresList.filter(
            (proc) =>
              proc.name.toLowerCase().includes(term) ||
              (proc.client_friendly_name && proc.client_friendly_name.toLowerCase().includes(term)) ||
              (proc.barcode && proc.barcode.toLowerCase().includes(term))
          );

    setProcedureSearchResults(results);
    setShowProcedureDropdown(true);
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      searchProcedures(procedureSearchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [procedureSearchTerm, proceduresList]);

  // ── Map procedure products ──
  const mapProcedureProducts = (procDoc) => {
    if (Array.isArray(procDoc?.products) && procDoc.products.length > 0) {
      return procDoc.products.map((item) => {
        const prod = item.product_id && typeof item.product_id === "object" ? item.product_id : null;
        const id = prod?._id || extractId(item.product_id);
        return {
          _id: id,
          name: prod?.name || "Unknown Product",
          sku_number: prod?.sku_number || "",
          product_unit: prod?.unit || "",
          product_price: prod?.sale_price || prod?.mrp || 0,
          usage_qty: item.usage_qty || item.amount_used || "",
          usage_unit: item.usage_unit || item.unit || "",
          price: item.price || 0,
        };
      });
    }
    return (procDoc?.product_ids || []).map((prod) => ({
      _id: extractId(prod) || prod,
      name: prod?.name || "Unknown Product",
      sku_number: prod?.sku_number || "",
      product_unit: prod?.unit || "",
      product_price: prod?.sale_price || prod?.mrp || 0,
      usage_qty: "",
      usage_unit: "",
      price: 0,
    }));
  };

  const fetchPlanDetails = async () => {
    setLoading(true);
    try {
      const response = await httpService.get(
        `/vendorvetclinicservice/get-treatment-plan/${planId}`
      );
      if (response.code === 200) {
        const data = response.data;

        const mappedProcedures = (data.procedures || []).map((p, idx) => {
          const procDoc = typeof p.procedure_id === "object" ? p.procedure_id : null;
          const procId = procDoc?._id || extractId(p.procedure_id);
          const actualPrice = procDoc?.fixed_price || 0;
          
          const products = mapProcedureProducts(procDoc);
          
          return {
            row_id: makeRowId(procId || idx),
            procedure_id: procId,
            procedure_name: procDoc?.name || p.procedure_name || "Unknown Procedure",
            occurrences: p.occurrences || 1,
            notes: p.notes || "",
            override_price: {
              low: actualPrice,
              high: actualPrice,
            },
            sequence: p.sequence ?? idx + 1,
            products: products,
            productsLoading: false,
          };
        });

        // Auto-expand procedures that have products
        const initialExpanded = {};
        mappedProcedures.forEach((proc) => {
          if (hasProducts(proc)) {
            initialExpanded[proc.row_id] = true;
          }
        });
        setExpandedProcedures(initialExpanded);

        const mappedPlanProducts = (data.products || []).map((item) => {
          const prod = item.product_id && typeof item.product_id === "object" ? item.product_id : null;
          const details = item.product_details || null;
          const id = details?._id || prod?._id || extractId(item.product_id);
          return {
            _id: id,
            name: details?.name || prod?.name || item.name || "Unknown Product",
            sku_number: details?.sku_number || prod?.sku_number || item.sku_number || "",
            price: Number(
              item.price ||
                details?.sale_price ||
                details?.mrp ||
                prod?.sale_price ||
                prod?.mrp ||
                0,
            ),
            quantity: item.quantity || 1,
            usage_qty: item.usage_qty || item.amount_used || "",
            usage_unit: item.usage_unit || item.unit || "",
            product_unit: details?.unit || prod?.unit || "",
            notes: item.notes || "",
          };
        });

        setPlanProducts(mappedPlanProducts);
        setFormData({
          name: data.name || "",
          description: data.description || "",
          procedures: mappedProcedures,
        });
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to load treatment plan details");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const toggleProcedureExpand = (rowId) => {
    setExpandedProcedures((prev) => ({
      ...prev,
      [rowId]: !prev[rowId],
    }));
  };

  // ── procedures: search and add ────────────────────────────────────────
  const fetchProcedureProducts = async (procedureId, rowId) => {
    try {
      const response = await httpService.get(
        `/vendorvetclinicservice/get-service-procedure/${procedureId}`
      );
      if (response.code !== 200) throw new Error("Failed to load procedure");

      const products = mapProcedureProducts(response.data);

      setFormData((prev) => ({
        ...prev,
        procedures: prev.procedures.map((p) =>
          p.row_id === rowId ? { ...p, products, productsLoading: false } : p
        ),
      }));

      // Auto-expand if products were found
      if (products.length > 0) {
        setExpandedProcedures((prev) => ({
          ...prev,
          [rowId]: true,
        }));
      }
    } catch (err) {
      console.error(err);
      setFormData((prev) => ({
        ...prev,
        procedures: prev.procedures.map((p) =>
          p.row_id === rowId ? { ...p, productsLoading: false } : p
        ),
      }));
    }
  };

  const handleAddProcedure = (procedure) => {
    if (!procedure) return;

    if (formData.procedures.some((p) => p.procedure_id === procedure._id)) {
      toast.info("This procedure is already added to the plan");
      return;
    }

    const actualPrice = procedure.fixed_price || 0;
    const rowId = makeRowId(procedure._id);
    const newRow = {
      row_id: rowId,
      procedure_id: procedure._id,
      procedure_name: procedure.name,
      occurrences: 1,
      notes: "",
      override_price: {
        low: actualPrice,
        high: actualPrice,
      },
      sequence: formData.procedures.length + 1,
      products: [],
      productsLoading: true,
    };

    setFormData((prev) => ({ ...prev, procedures: [...prev.procedures, newRow] }));
    setProcedureSearchTerm("");
    setProcedureSearchResults([]);
    setShowProcedureDropdown(false);
    if (procedureInputRef.current) procedureInputRef.current.focus();
    
    fetchProcedureProducts(procedure._id, rowId);
  };

  const handleRemoveProcedure = (index) => {
    setFormData((prev) => ({
      ...prev,
      procedures: prev.procedures
        .filter((_, i) => i !== index)
        .map((p, i) => ({ ...p, sequence: i + 1 })),
    }));
  };

  const handleProcedureChange = (index, field, value) => {
    setFormData((prev) => {
      const updated = [...prev.procedures];
      updated[index] = { ...updated[index], [field]: value };
      return { ...prev, procedures: updated };
    });
  };

  // ── Plan Products (extra products at treatment plan level) ────────────
  const searchPlanProducts = async (term) => {
    if (!term || term.length < 2) {
      setProductSearchResults([]);
      setShowProductDropdown(false);
      return;
    }
    setIsSearchingProducts(true);
    try {
      const response = await httpService.get(
        `/vendorproduct/searchvendorproducts?search=${encodeURIComponent(term)}`
      );
      if (response.code === 200) {
        const selectedIds = new Set(planProducts.map((p) => p._id));
        const filteredResults = response.data.filter((p) => !selectedIds.has(p._id));
        setProductSearchResults(filteredResults);
        setShowProductDropdown(true);
      }
    } catch (err) {
      console.error("Failed to search products:", err);
    } finally {
      setIsSearchingProducts(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      if (productSearchTerm) searchPlanProducts(productSearchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [productSearchTerm]);

  const handleAddPlanProduct = (product) => {
    setPlanProducts((prev) => [
      ...prev,
      {
        _id: product._id,
        name: product.name,
        sku_number: product.sku_number || "",
        price: product.sale_price || product.mrp || 0,
        quantity: 1,
        product_unit: product.unit || "",
        usage_qty: "",
        usage_unit: "",
        notes: "",
      },
    ]);
    setProductSearchTerm("");
    setProductSearchResults([]);
    setShowProductDropdown(false);
    if (productInputRef.current) productInputRef.current.focus();
  };

  const handleNewProductCreated = (newProduct) => {
    setShowAddProductDrawer(false);

    if (!newProduct) return;

    // Case 1: simple product (no variants) — add it directly
    if (!newProduct.has_variants) {
      handleAddPlanProduct({
        _id: newProduct._id,
        name: newProduct.name,
        sku_number: newProduct.sku_number,
        unit: newProduct.unit,
        sale_price: newProduct.mrp,
        mrp: newProduct.mrp,
      });
      return;
    }

    // Case 2: product created with variants — add every variant as its own row
    const variants = newProduct.variants || [];
    if (variants.length === 0) {
      toast.info("Product created, but no variants were found to add.");
      return;
    }

    variants.forEach((variant) => {
      handleAddPlanProduct({
        _id: variant._id || `${newProduct._id}-${variant.sku_number}`,
        name: `${newProduct.name}${variant.variant_name ? " - " + variant.variant_name : ""}`,
        sku_number: variant.sku_number,
        unit: variant.unit || newProduct.unit,
        sale_price: variant.mrp,
        mrp: variant.mrp,
      });
    });

    toast.success(`${variants.length} variant(s) added to this plan`);
  };

  const handlePlanProductQuantity = (index, delta) => {
    setPlanProducts((prev) => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        quantity: Math.max(1, (updated[index].quantity || 1) + delta),
      };
      return updated;
    });
  };

  const handlePlanProductQuantityInput = (index, value) => {
    const qty = Math.max(1, parseInt(value, 10) || 1);
    setPlanProducts((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], quantity: qty };
      return updated;
    });
  };

  const handlePlanProductFieldChange = (index, field, value) => {
    setPlanProducts((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const handleRemovePlanProduct = (index) => {
    setPlanProducts((prev) => prev.filter((_, i) => i !== index));
  };

  const planProductsSubtotal = useMemo(
    () => planProducts.reduce((sum, p) => sum + (p.price || 0) * (p.quantity || 1), 0),
    [planProducts]
  );

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      toast.error("Plan name is required");
      return;
    }

    if (formData.procedures.length === 0 && planProducts.length === 0) {
      toast.error("Please add at least one procedure or product to the plan");
      return;
    }

    setLoading(true);
    try {
      const url = planId
        ? `/vendorvetclinicservice/update-treatment-plan/${planId}`
        : "/vendorvetclinicservice/add-treatment-plan";
      const method = planId ? "put" : "post";

      const payload = {
        name: formData.name,
        description: formData.description,
        status: "active",
        procedures: formData.procedures.map((p, idx) => ({
          procedure_id: p.procedure_id,
          occurrences: p.occurrences || 1,
          notes: p.notes,
          override_price: p.override_price || null,
          sequence: idx + 1,
        })),
        products: planProducts.map((p) => ({
          product_id: p._id,
          quantity: p.quantity || 1,
          price: p.price || 0,
          usage_qty: parseFloat(p.usage_qty) || 0,
          usage_unit: p.usage_unit || "",
          notes: p.notes || "",
        })),
      };

      const response = await httpService[method](url, payload);
      if (response.status) {
        toast.success(
          planId
            ? "Treatment plan updated successfully!"
            : "Treatment plan created successfully!"
        );
        onSuccess?.();
        onClose();
      }
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.message || "Failed to save treatment plan");
    } finally {
      setLoading(false);
    }
  };

  const calculateTotals = () => {
    let total = 0;

    formData.procedures.forEach((p) => {
      const occurrences = Number(p.occurrences) || 1;
      total += (Number(p.override_price?.low) || 0) * occurrences;
    });

    return {
      total: total + planProductsSubtotal,
      procedureCount: formData.procedures.length,
      productCount: planProducts.length,
      planProductsSubtotal,
    };
  };

  const totals = calculateTotals();
  const hasItems = formData.procedures.length > 0 || planProducts.length > 0;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      <div className="absolute inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose} />

      <div className="absolute inset-y-0 right-0 max-w-full flex">
        <div className="relative w-[1100px] max-w-[98vw]">
          <div className="h-full flex flex-col bg-white shadow-xl overflow-hidden">
            <div className="px-6 py-4 bg-gray-50 border-b border-gray-200 shrink-0">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-medium text-gray-900">
                  {planId ? "Edit Treatment Plan" : "Add New Treatment Plan"}
                </h2>
                <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
                  <span className="sr-only">Close</span>
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
            </div>

            {loading ? (
              <div className="flex-1 flex items-center justify-center">
                <LoaderSpiner text="Loading..." />
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="flex-1 flex flex-col overflow-hidden">
                <div className="flex-1 overflow-y-auto px-6 py-6 space-y-6">
                  {/* Plan Name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Plan Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder="e.g., Annual Health Check, Dental Treatment"
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    />
                  </div>

                  {/* ── Searchable Procedures ── */}
                  <div>
                    <h3 className="text-md font-medium text-gray-900 mb-3">Add Services/Procedures</h3>

                    <div className="relative" ref={procedureDropdownRef}>
                      <div className="relative">
                        <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <input
                          ref={procedureInputRef}
                          type="text"
                          value={procedureSearchTerm}
                          onChange={(e) => setProcedureSearchTerm(e.target.value)}
                          onFocus={() => {
                            searchProcedures(procedureSearchTerm);
                          }}
                          placeholder="Search procedures by name..."
                          className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        />
                      </div>

                      {showProcedureDropdown && (
                        <div className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                          {procedureSearchResults.length > 0 ? (
                            procedureSearchResults.map((procedure) => {
                              const price = getProcedurePrice(procedure);
                              const priceDisplay = `₹${price}`;
                              return (
                                <div
                                  key={procedure._id}
                                  className="px-4 py-2 hover:bg-gray-100 cursor-pointer flex items-center justify-between"
                                  onClick={() => handleAddProcedure(procedure)}
                                >
                                  <div>
                                    <div className="text-sm font-medium text-gray-900">{procedure.name}</div>
                                    <div className="text-xs text-gray-500">
                                      {priceDisplay}
                                    </div>
                                  </div>
                                  <button
                                    type="button"
                                    className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                                    disabled={formData.procedures.some((p) => p.procedure_id === procedure._id)}
                                  >
                                    {formData.procedures.some((p) => p.procedure_id === procedure._id)
                                      ? "Added ✓"
                                      : "Add"}
                                  </button>
                                </div>
                              );
                            })
                          ) : (
                            <div className="px-4 py-2 text-sm text-gray-500">No procedures found</div>
                          )}
                        </div>
                      )}
                    </div>
                    <p className="mt-1 text-xs text-gray-400">
                      Search and select a service/procedure to add to this treatment plan.
                    </p>
                  </div>

                  {/* ── Added Procedures Table ── */}
                  {formData.procedures.length > 0 ? (
                    <div className="overflow-x-auto border border-gray-200 rounded-md">
                      <table className="min-w-full w-full divide-y divide-gray-200 text-sm">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-2 py-1 text-left font-medium text-gray-500 w-[250px]">Service/Procedure</th>
                            <th className="px-3 py-2 text-left font-medium text-gray-500 w-[120px]">Occurrences</th>
                            <th className="px-3 py-2 text-left font-medium text-gray-500 w-[140px]">Price</th>
                            <th className="px-3 py-2 text-left font-medium text-gray-500 w-[140px]">Products Used</th>
                            <th className="px-3 py-2 w-10" />
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {formData.procedures.map((proc, procIndex) => {
                            const priceDisplay = `₹${proc.override_price?.low || 0}`;
                            return (
                              <React.Fragment key={proc.row_id}>
                                <tr>
                                  <td className="px-3 py-2 font-medium text-gray-900">
                                    {proc.procedure_name}
                                  </td>
                                  <td className="px-3 py-2">
                                    <div className="flex items-center gap-2">
                                      <button
                                        type="button"
                                        onClick={() => {
                                          const newVal = Math.max(1, (proc.occurrences || 1) - 1);
                                          handleProcedureChange(procIndex, "occurrences", newVal);
                                        }}
                                        disabled={proc.occurrences <= 1}
                                        className="h-7 w-7 flex items-center justify-center rounded-md border border-gray-300 text-gray-600 hover:bg-gray-100 disabled:opacity-40"
                                      >
                                        <MinusIcon className="h-3.5 w-3.5" />
                                      </button>
                                      <input
                                        type="number"
                                        min="1"
                                        value={proc.occurrences || 1}
                                        onChange={(e) =>
                                          handleProcedureChange(procIndex, "occurrences", Math.max(1, parseInt(e.target.value) || 1))
                                        }
                                        className="w-14 text-center border border-gray-300 rounded-md py-1 text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                      />
                                      <button
                                        type="button"
                                        onClick={() => {
                                          const newVal = (proc.occurrences || 1) + 1;
                                          handleProcedureChange(procIndex, "occurrences", newVal);
                                        }}
                                        className="h-7 w-7 flex items-center justify-center rounded-md border border-gray-300 text-gray-600 hover:bg-gray-100"
                                      >
                                        <PlusIcon className="h-3.5 w-3.5" />
                                      </button>
                                    </div>
                                  </td>
                                  <td className="px-3 py-2">
                                    <div className="relative w-24">
                                      <FaIndianRupeeSign className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-gray-400" />
                                      <input
                                        type="number"
                                        min="0"
                                        value={proc.override_price?.low || 0}
                                        onChange={(e) =>
                                          handleProcedureChange(procIndex, "override_price", {
                                            ...proc.override_price,
                                            low: parseFloat(e.target.value) || 0,
                                            high: parseFloat(e.target.value) || 0,
                                          })
                                        }
                                        className="w-full pl-5 pr-1 border border-gray-300 rounded-md py-1 text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                      />
                                    </div>
                                  </td>
                                  <td className="px-3 py-2">
                                    <button
                                      type="button"
                                      onClick={() => toggleProcedureExpand(proc.row_id)}
                                      className="flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-800"
                                    >
                                      {expandedProcedures[proc.row_id] ? (
                                        <ChevronDownIcon className="h-3.5 w-3.5" />
                                      ) : (
                                        <ChevronRightIcon className="h-3.5 w-3.5" />
                                      )}
                                      {proc.productsLoading
                                        ? "Loading..."
                                        : `${proc.products.length} product${proc.products.length === 1 ? "" : "s"}`}
                                    </button>
                                  </td>
                                  <td className="px-3 py-2 text-center">
                                    <button
                                      type="button"
                                      onClick={() => handleRemoveProcedure(procIndex)}
                                      className="text-gray-400 hover:text-red-600"
                                    >
                                      <TrashIcon className="h-4 w-4" />
                                    </button>
                                  </td>
                                </tr>
                                {/* Expanded products within procedure */}
                                {expandedProcedures[proc.row_id] && (
                                  <tr>
                                    <td colSpan={5} className="bg-gray-50 px-4 py-3">
                                      {proc.productsLoading ? (
                                        <p className="text-xs text-gray-400">Loading products...</p>
                                      ) : proc.products.length > 0 ? (
                                        <div className="overflow-x-auto">
                                          <table className="min-w-full divide-y divide-gray-200 text-sm">
                                            <thead>
                                              <tr>
                                                <th className="px-2 py-1 text-left font-medium text-gray-500 w-[250px]">Product</th>
                                                <th className="px-3 py-2 text-left font-medium text-gray-500 w-[100px]">Unit</th>
                                                <th className="px-3 py-2 text-left font-medium text-gray-500 w-[120px]">Usage</th>
                                                <th className="px-3 py-2 text-center font-medium text-gray-500 w-[80px]">Qty</th>
                                                <th className="px-3 py-2 text-right font-medium text-gray-500 w-[100px]">Price</th>
                                              </tr>
                                            </thead>
                                            <tbody>
                                              {proc.products.map((product, prodIndex) => {
                                                const usageDisplay = product.usage_qty && product.usage_unit 
                                                  ? `${product.usage_qty} ${product.usage_unit}` 
                                                  : product.usage_qty || "—";
                                                const productUnit = product.product_unit || "—";
                                                const productPrice = product.product_price || product.price || 0;
                                                return (
                                                  <tr key={product._id || prodIndex}>
                                                    <td className="px-3 py-1">
                                                      <div className="font-medium text-gray-900">{product.name}</div>
                                                      {product.sku_number && (
                                                        <div className="text-xs text-gray-500">SKU: {product.sku_number}</div>
                                                      )}
                                                    </td>
                                                    <td className="px-3 py-1 text-gray-700">
                                                      {productUnit}
                                                    </td>
                                                    <td className="px-3 py-1 text-gray-700">
                                                      {usageDisplay}
                                                    </td>
                                                    <td className="px-3 py-1 text-center text-gray-700">
                                                      {proc.occurrences || 1}
                                                    </td>
                                                    <td className="px-3 py-1 text-right font-medium text-gray-900">
                                                      ₹{productPrice.toFixed(2)}
                                                    </td>
                                                  </tr>
                                                );
                                              })}
                                            </tbody>
                                          </table>
                                        </div>
                                      ) : (
                                        <p className="text-xs text-gray-400">No products linked to this procedure.</p>
                                      )}
                                    </td>
                                  </tr>
                                )}
                              </React.Fragment>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="text-sm text-gray-400">No services/procedures added yet.</p>
                  )}

                  {/* ── Additional Products ── */}
                  <div>
                    <h3 className="text-md font-medium text-gray-900 mb-3">Additional Products</h3>

                    <div className="relative" ref={productDropdownRef}>
                      <div className="relative">
                        <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <input
                          ref={productInputRef}
                          type="text"
                          value={productSearchTerm}
                          onChange={(e) => setProductSearchTerm(e.target.value)}
                          onFocus={() => {
                            if (productSearchResults.length > 0) setShowProductDropdown(true);
                          }}
                          placeholder="Search and add extra products..."
                          className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        />
                      </div>

                      {showProductDropdown && (
                        <div className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                          {isSearchingProducts ? (
                            <div className="px-4 py-2 text-sm text-gray-500">Searching...</div>
                          ) : productSearchResults.length > 0 ? (
                            productSearchResults.map((product) => (
                              <div
                                key={product._id}
                                className="px-4 py-2 hover:bg-gray-100 cursor-pointer flex items-center justify-between"
                                onClick={() => handleAddPlanProduct(product)}
                              >
                                <div>
                                  <div className="text-sm font-medium text-gray-900">{product.name}</div>
                                  <div className="text-xs text-gray-500">
                                    SKU: {product.sku_number || "N/A"} | ₹{product.sale_price || product.mrp || 0}/{product.unit || "unit"}
                                  </div>
                                </div>
                                <button type="button" className="text-blue-600 hover:text-blue-800 text-sm">
                                  Add
                                </button>
                              </div>
                            ))
                          ) : productSearchTerm.length >= 2 ? (
                            <div className="px-4 py-2 text-sm">
                              <p className="text-gray-500">
                                No products found{productSearchTerm ? ` for "${productSearchTerm}"` : ""}
                              </p>
                              <button
                                type="button"
                                onClick={() => {
                                  setShowProductDropdown(false);
                                  setShowAddProductDrawer(true);
                                }}
                                className="mt-2 text-blue-600 font-medium hover:underline flex items-center gap-1"
                              >
                                <PlusIcon className="h-4 w-4" />
                                Add {productSearchTerm ? `"${productSearchTerm}"` : ""} as a new product
                              </button>
                            </div>
                          ) : null}
                        </div>
                      )}
                    </div>

                    {planProducts.length > 0 ? (
                      <div className="mt-3 overflow-x-auto border border-gray-200 rounded-md">
                        <table className="min-w-full w-full divide-y divide-gray-200 text-sm">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase">Product</th>
                              <th className="px-3 py-2 text-center text-xs font-semibold text-gray-500 uppercase">
                                <div className="flex items-center justify-center gap-1">
                                  <span>Usage (Qty × Unit)</span>
                                  <InfoTooltip>
                                    <div className="space-y-2">
                                      <p className="font-medium text-white">How to use this field:</p>
                                      <ul className="list-disc pl-4 space-y-1 text-gray-300">
                                        <li>Enter the quantity used per occurrence (e.g., 10 ml)</li>
                                        <li>If using the <span className="text-yellow-300 font-medium">whole product</span>, set this to 1</li>
                                        <li>Select the appropriate unit from the dropdown</li>
                                      </ul>
                                      <div className="mt-2 p-2 bg-gray-700 rounded">
                                        <p className="text-gray-300 text-xs">Example: 10 ml from a vial = 10 × ml</p>
                                        <p className="text-gray-300 text-xs mt-1">Example: Whole vial = 1 × Vial</p>
                                      </div>
                                    </div>
                                  </InfoTooltip>
                                </div>
                              </th>
                              <th className="px-3 py-2 text-center text-xs font-semibold text-gray-500 uppercase">Qty Used</th>
                              <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase">Unit</th>
                              <th className="px-3 py-2 text-right text-xs font-semibold text-gray-500 uppercase">Subtotal</th>
                              <th className="px-3 py-2 w-10" />
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100">
                            {planProducts.map((product, index) => {
                              const subtotal = Number(product.price || 0) * Number(product.quantity || 1);
                              return (
                                <tr key={product._id || index}>
                                  <td className="px-3 py-2">
                                    <div className="font-medium text-gray-900 text-sm">{product.name}</div>
                                    <div className="text-xs text-gray-500">
                                      SKU: {product.sku_number || "—"} | ₹{Number(product.price || 0).toFixed(2)}/{product.product_unit || "unit"}
                                    </div>
                                  </td>
                                  <td className="px-3 py-2">
                                    <div className="flex items-center justify-center gap-1">
                                      <input
                                        type="number"
                                        min="0"
                                        step="0.1"
                                        value={product.usage_qty || ""}
                                        onChange={(e) => handlePlanProductFieldChange(index, "usage_qty", e.target.value)}
                                        placeholder="0"
                                        className="w-16 text-center border border-gray-300 rounded-md py-1 px-2 text-sm"
                                      />
                                      <span className="text-gray-400 text-sm">×</span>
                                      <select
                                        value={product.usage_unit || ""}
                                        onChange={(e) => handlePlanProductFieldChange(index, "usage_unit", e.target.value)}
                                        className="border border-gray-300 rounded-md py-1 px-2 text-sm bg-white w-20"
                                      >
                                        <option value="">Unit</option>
                                        <option value="ml">ml</option>
                                        <option value="l">L</option>
                                        <option value="gm">gm</option>
                                        <option value="kg">kg</option>
                                        <option value="mg">mg</option>
                                        <option value="vial">Vial</option>
                                        <option value="tablet">Tablet</option>
                                        <option value="pcs">Pcs</option>
                                        <option value="bottle">Bottle</option>
                                        <option value="ampoule">Ampoule</option>
                                        <option value="syringe">Syringe</option>
                                        <option value="dose">Dose</option>
                                        <option value="drop">Drop</option>
                                        <option value="spray">Spray</option>
                                        <option value="tube">Tube</option>
                                        <option value="pack">Pack</option>
                                      </select>
                                    </div>
                                  </td>
                                  <td className="px-3 py-2">
                                    <div className="flex items-center justify-center gap-2">
                                      <button
                                        type="button"
                                        onClick={() => handlePlanProductQuantity(index, -1)}
                                        disabled={product.quantity <= 1}
                                        className="h-7 w-7 flex items-center justify-center rounded-md border border-gray-300 text-gray-600 hover:bg-gray-100 disabled:opacity-40"
                                      >
                                        <MinusIcon className="h-3.5 w-3.5" />
                                      </button>
                                      <input
                                        type="number"
                                        min="1"
                                        value={product.quantity}
                                        onChange={(e) => handlePlanProductQuantityInput(index, e.target.value)}
                                        className="w-12 text-center border border-gray-300 rounded-md py-1 text-sm"
                                      />
                                      <button
                                        type="button"
                                        onClick={() => handlePlanProductQuantity(index, 1)}
                                        className="h-7 w-7 flex items-center justify-center rounded-md border border-gray-300 text-gray-600 hover:bg-gray-100"
                                      >
                                        <PlusIcon className="h-3.5 w-3.5" />
                                      </button>
                                    </div>
                                  </td>
                                  <td className="px-3 py-2 text-left">
                                    <span className="text-sm text-gray-700">{product.product_unit || "—"}</span>
                                  </td>
                                  <td className="px-3 py-2 text-right font-medium text-gray-900">
                                    ₹{subtotal.toFixed(2)}
                                  </td>
                                  <td className="px-3 py-2 text-center">
                                    <button
                                      type="button"
                                      onClick={() => handleRemovePlanProduct(index)}
                                      className="text-gray-400 hover:text-red-600"
                                    >
                                      <TrashIcon className="h-4 w-4" />
                                    </button>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                          <tfoot className="bg-gray-50">
                            <tr>
                              <td colSpan={4} className="px-3 py-2 text-right text-sm font-medium text-gray-600">
                                Total Additional Products Cost
                              </td>
                              <td className="px-3 py-2 text-right text-sm font-semibold text-gray-900">
                                ₹{planProductsSubtotal.toFixed(2)}
                              </td>
                              <td />
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    ) : (
                      <p className="mt-3 text-sm text-gray-400">No extra products added yet.</p>
                    )}
                  </div>

                  {/* Description */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Description</label>
                    <textarea
                      name="description"
                      value={formData.description}
                      onChange={handleChange}
                      rows={3}
                      placeholder="Describe the treatment plan"
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    />
                  </div>
                </div>

                {/* ── Static summary ── */}
                {hasItems && (
                  <div className="shrink-0 bg-blue-50 border-t border-blue-200 px-6 py-4">
                    <div className="grid grid-cols-3 gap-4 text-sm">
                      <div>
                        <span className="text-gray-600">Services:</span>
                        <span className="ml-2 font-medium">{totals.procedureCount}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Extra Products:</span>
                        <span className="ml-2 font-medium">{totals.productCount}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Total Cost:</span>
                        <span className="ml-2 font-medium">
                          <FaIndianRupeeSign className="inline h-3 w-3" />
                          {totals.total.toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {/* ── Static footer ── */}
                <div className="shrink-0 flex justify-end space-x-3 px-6 py-4 border-t border-gray-200 bg-white">
                  <button
                    type="button"
                    onClick={onClose}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? "Saving..." : planId ? "Update" : "Create"}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
      <ProductDrawer
        isOpen={showAddProductDrawer}
        onClose={() => setShowAddProductDrawer(false)}
        newProductName={productSearchTerm}
        onSave={handleNewProductCreated}
      />
    </div>
  );
};

export default TreatmentPlanDrawer;